<template>
  <WCSinglePageForm>
    <template v-slot:title>
      {{ $t('createAccount') }}
    </template>

    <template v-slot:title-right>
      <label>
        {{ $t('haveAnAccount') }}
        <router-link class="font-weight-bold underline-link-from-center" to="/login">
          {{ $t('signIn') }}</router-link
        >
      </label>
    </template>

    <template v-slot:subheading>
      {{ $t('thankYouForCreatingAccount') }}
    </template>

    <template v-slot:body>
      <form @submit.prevent="submit">
        <WCFormGroup :field="$v.form.firstName">
          <label for="register-form__first-name"
            >{{ $t('firstName') }}<span class="text-danger"> *</span></label
          >
          <WCValidatedInput
            id="register-form__first-name"
            v-model="form.firstName"
            :vuelidateObject="$v.form.firstName"
            autocomplete="section-register given-name"
            >{{ $t('firstNameIsRequired') }}</WCValidatedInput
          >
          <WCValidationErrors />
        </WCFormGroup>
        <WCFormGroup :field="$v.form.lastName">
          <label for="register-form__last-name"
            >{{ $t('lastName') }}<span class="text-danger"> *</span></label
          >
          <WCValidatedInput
            id="register-form__last-name"
            v-model="form.lastName"
            :vuelidate-object="$v.form.lastName"
            autocomplete="section-register family-name"
            >{{ $t('lastNameIsRequired') }}</WCValidatedInput
          >
          <WCValidationErrors />
        </WCFormGroup>
        <WCFormGroup :field="$v.form.email">
          <label for="register-form__email"
            >{{ $t('emailAddress') }}<span class="text-danger"> *</span></label
          >
          <WCValidatedInput
            id="register-form__email"
            v-model="form.email"
            :vuelidate-object="$v.form.email"
            autocomplete="section-register email"
            >{{ $t('emailIsRequired') }}</WCValidatedInput
          >
          <WCValidationErrors />
        </WCFormGroup>
        <WCFormGroup :field="$v.form.phone">
          <label for="register-form__phone"
            >{{ $t('phoneNumber') }}<span class="text-danger"> *</span></label
          >
          <WCValidatedInput
            id="register-form__phone"
            v-model="form.phone"
            :vuelidate-object="$v.form.phone"
            autocomplete="section-register tel-national"
            @input="changePhoneNumberFormat(form.phone)"
            >{{ $t('phoneNumberIsRequired') }}</WCValidatedInput
          >
          <WCValidationErrors />
        </WCFormGroup>
        <WCFormGroup>
          <label for="register-form__password"
            >{{ $t('createAPassword') }}<span class="text-danger"> *</span></label
          >
          <WCNewPasswordInput
            id="register-form__password"
            v-model="form.password"
            autocomplete="section-register new-password"
            :$v="$v"
          />
        </WCFormGroup>
        <WCFormGroup>
          <WCCheckbox :id="'register-form__receipts'" v-model="form.receipts">
            {{ $t('goGreen') }}
            <font-awesome-icon class="text-success" :icon="'leaf'" />
            <div class="font-size-xs">
              {{ $t('checkTheBoxForReceipts') }}
            </div>
          </WCCheckbox>
        </WCFormGroup>
        <WCFormGroup>
          <WCCheckbox :id="'register-form__marketing'" v-model="form.marketing">
            {{ $t('joinPromosAndRewards') }}
            <font-awesome-icon class="text-primary" :icon="'gift'" />
            <div class="font-size-xs">
              {{ $t('optInToAllowInfo') }}
            </div>
          </WCCheckbox>
        </WCFormGroup>
        <WCInvisibleRecaptcha ref="recaptcha" />
        <button class="btn btn-primary btn-block submit-btn" :disabled="$v.form.$invalid">
          {{ $t('createAccount') }}
        </button>
      </form>
    </template>
  </WCSinglePageForm>
</template>

<script>
import { required, email } from 'vuelidate/lib/validators';
import { phone, password } from '@/utils/validators/validators';
import WCFormGroup from '@/modules/forms/components/WCFormGroup/WCFormGroup.vue';
import WCValidatedInput from '@/modules/forms/components/WCValidatedInput/WCValidatedInput.vue';
import WCCheckbox from '@/modules/forms/components/WCCheckbox/WCCheckbox.vue';
import WCInvisibleRecaptcha from '@/modules/recaptcha/components/WCInvisibleRecaptcha/WCInvisibleRecaptcha.vue';
import WCSinglePageForm from '@/modules/forms/components/WCSinglePageForm/WCSinglePageForm.vue';
import WCNewPasswordInput from '@/modules/forms/components/WCNewPasswordInput/WCNewPasswordInput.vue';
import WCValidationErrors from '@/modules/forms/components/WCValidationErrors/WCValidationErrors.vue';
import PhoneNumberFormatMixin from '@/mixins/PhoneNumberFormatMixin';

export default {
  name: 'WCRegisterForm',
  components: {
    WCNewPasswordInput,
    WCFormGroup,
    WCCheckbox,
    WCInvisibleRecaptcha,
    WCSinglePageForm,
    WCValidatedInput,
    WCValidationErrors,
  },
  mixins: [PhoneNumberFormatMixin],
  data() {
    return {
      form: {
        firstName: '',
        lastName: '',
        email: '',
        phone: '',
        password: '',
        receipts: false,
        marketing: false,
      },
    };
  },
  validations: {
    form: {
      firstName: {
        required,
      },
      lastName: {
        required,
      },
      email: {
        required,
        email,
      },
      phone: {
        required,
        phone,
      },
      password: {
        required,
        password: password(),
      },
    },
  },
  methods: {
    async submit() {
      try {
        const payload = {
          ...this.form,
          recaptcha: await this.$refs.recaptcha.execute(),
        };
        this.$emit('submit', payload);
        this.reset();
      } catch (err) {
        console.error('Cannot execute recaptcha');
      }
    },
    reset() {
      this.firstName = '';
      this.lastName = '';
      this.email = '';
      this.phone = '';
      this.password = '';
      this.receipts = false;
      this.marketing = false;
      this.$v.form.$reset();

      this.$refs.recaptcha.reset();
    },
    /**
     * Returns the formatted phone number
     * Uses PhoneNumberFormatMixin
     */
    changePhoneNumberFormat(phoneNumber) {
      this.form.phone = this.formatPhoneNumber(phoneNumber);
    },
  },
};
</script>

<style scoped lang="scss"></style>
